import * as React from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import { useI18n } from "../i18n/I18nProvider";


type Props = {
  handlePageChange: (page: string) => void;
  role: string;
};

export default function PrivacyPolicy(props: Props) {
  const { handlePageChange, role } = props;

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const { lang } = useI18n();

  const content = {
    EN: {
      title: "Privacy Policy",
      updated: "Last updated",
      updatedDate: "July 28, 2026",
      intro:
        "This Privacy Policy explains how takeKONTROL collects, uses, stores, and protects personal data when you register for or use the platform.",
      sections: [
        {
          title: "1. Data We Collect",
          body: [
            "Account and authentication data: company email address, Supabase user ID, confirmation and password reset records, session and security logs, and authentication provider information.",
            "Company and role data: company affiliation, department, company role such as employee, manager, or admin, workshop participation, and manager registration code usage where applicable.",
            "Optional profile data: first name, last name, position, personal address, age, location, gender, avatar image path, and anonymity settings. If you register anonymously, identifying profile fields are not stored for that mode.",
            "Preparedness assessment data: answers to PBG, EKS, ExtPRS, vibe, availability, household, practical, health, mobility, social support, documents, transport, regional risk, and similar preparedness questions.",
            "Workshop data: stay-or-leave decisions, selected threats, priorities, logistics, reflections, document choices, blackout knowledge, physical limitation, information filtering, isolation stability, resilience behavior, invitation requests, and related workshop progress.",
            "Lab photo data: photos that you voluntarily upload during a lab or workshop photo task, including the storage path and related file metadata.",
            "Generated data: Kontrol scores, LAB scores, score bands, availability signals, assessment flags, skipped or missing question audit data, recommendations, module completion, task progress, and answer improvement records.",
            "Support and contact data: messages submitted through customer question or support features and invited email addresses entered when you invite another person.",
          ],
        },
        {
          title: "2. Purpose of Processing",
          body: [
            "We process your data to create and manage your account, confirm your email, reset passwords, connect your account to your employee profile, and operate core platform features.",
            "We use assessment, workshop, and module data to calculate scores, show progress, generate recommendations, support preparedness exercises, and provide individual or group workshop views.",
            "We use uploaded lab photos to support the lab photo task, allow authorized managers or admins to view photos from the lab, and document voluntary participation in that activity.",
            "We use company and role data to apply access permissions, enable manager or admin dashboards, manage workshops, and connect employees to the correct organization.",
            "We use support messages and invitation requests to respond to questions and support the platform's invite and sharing workflows.",
          ],
        },
        {
          title: "3. Automated Processing and Scoring",
          body: [
            "The platform automatically evaluates selected answers and progress data to calculate scores, score bands, recommendations, flags, and next actions.",
            "These outputs are based on predefined scoring rules and logic in the platform. They are informational tools for awareness and preparedness.",
            "The outputs do not create legal effects, do not guarantee safety, and should not be treated as professional, medical, legal, or emergency advice.",
          ],
        },
        {
          title: "4. Organizational Visibility",
          body: [
            "If you use takeKONTROL through an employer, company, workshop, or similar organization, your account may be linked to that organization.",
            "Managers and admins may see data needed to run workshops, manage participation, view company-level dashboards, and understand group readiness. Depending on implemented permissions, this may include employee profile fields, participation status, scores, selected workshop answers, or aggregated insights.",
            "Uploaded lab photos may be visible to authorized managers and admins through the Lab Photos gallery.",
            "Some workshop screens intentionally show group responses or percentages to participants. Where anonymity is enabled, direct identifying profile fields are reduced, but some workshop choices may still be visible as part of the group experience.",
          ],
        },
        {
          title: "5. Storage and Service Providers",
          body: [
            "Platform data is stored in Supabase, including PostgreSQL database tables, Supabase Auth, Edge Functions, and Supabase Storage for avatar files and voluntarily uploaded lab photos.",
            "Email confirmation and password reset emails may be sent through email service providers such as Resend.",
            "We do not sell personal data. We share data only with service providers needed to operate, secure, host, maintain, or support the platform.",
          ],
        },
        {
          title: "6. Security",
          body: [
            "We use Supabase authentication, access controls, Row-Level Security policies, role-based permissions, and protected Edge Functions to limit access to personal data.",
            "Many tables restrict users to their own records, while managers and admins receive access needed for organizational and workshop administration.",
            "No system can be guaranteed completely secure, but we take reasonable technical and organizational measures to protect the platform.",
          ],
        },
        {
          title: "7. Retention and Deletion",
          body: [
            "We retain personal data for as long as needed to provide the service, maintain your account, support workshops, comply with legal obligations, resolve disputes, and protect platform integrity.",
            "Voluntarily uploaded lab photos are stored until you request deletion, unless they are deleted earlier as part of account deletion, workshop administration, or platform maintenance.",
            "If your account is not used for one year, measured from the latest known sign-in or session activity, we automatically delete the account and linked platform data. For accounts that never sign in, the one-year period starts on the account creation date.",
            "You may request account deletion through the platform where available. Account deletion removes the Supabase auth user and related records that are configured to cascade or be deleted by the platform.",
            "Some aggregated, anonymized, backup, audit, or legally required records may be retained where they no longer directly identify you or where retention is required.",
          ],
        },
        {
          title: "8. Your Rights",
          body: [
            "Depending on applicable law, you may have rights to access, correct, delete, restrict, object to processing, or receive a copy of your personal data.",
            "You may update certain profile data through the platform, switch anonymity settings where available, request an export, or request deletion.",
            "To exercise rights that are not available directly in the product, contact us using the email below.",
          ],
        },
        {
          title: "9. Cookies and Local Storage",
          body: [
            "We use technical cookies, browser storage, and Supabase session storage required for authentication, security, language selection, cached role data, and workshop flow continuity.",
            "We do not use advertising cookies in the platform.",
          ],
        },
        {
          title: "10. Changes to this Policy",
          body: [
            "We may update this Privacy Policy to reflect product, data model, provider, or legal changes.",
            "Material updates will be made available in the platform. Continued use after an update means the current policy applies to future use.",
          ],
        },
        {
          title: "11. Contact",
          body: [
            "For questions about privacy or data protection, contact: info@takekontrol.de",
          ],
        },
      ],
    },

    DE: {
      title: "Datenschutzerklärung",
      updated: "Zuletzt aktualisiert",
      updatedDate: "28. Juli 2026",
      intro:
        "Diese Datenschutzerklärung erläutert, wie takeKONTROL personenbezogene Daten erhebt, nutzt, speichert und schützt, wenn Sie sich registrieren oder die Plattform verwenden.",
      sections: [
        {
          title: "1. Erhobene Daten",
          body: [
            "Konto- und Authentifizierungsdaten: Firmen-E-Mail-Adresse, Supabase-Benutzer-ID, Bestätigungs- und Passwort-Reset-Datensätze, Sitzungs- und Sicherheitsprotokolle sowie Informationen zum Authentifizierungsanbieter.",
            "Unternehmens- und Rollendaten: Unternehmenszuordnung, Abteilung, Rolle im Unternehmen wie Employee, Manager oder Admin, Workshop-Teilnahme und gegebenenfalls Nutzung von Manager-Registrierungscodes.",
            "Optionale Profildaten: Vorname, Nachname, Position, private Adresse, Alter, Standort, Geschlecht, Avatar-Dateipfad und Anonymitätseinstellungen. Bei anonymer Registrierung werden identifizierende Profilfelder für diesen Modus nicht gespeichert.",
            "Preparedness- und Assessment-Daten: Antworten zu PBG, EKS, ExtPRS, Vibe, Verfügbarkeit, Haushalt, praktischer Vorbereitung, Gesundheit, Mobilität, sozialer Unterstützung, Dokumenten, Transport, regionalen Risiken und ähnlichen Fragen.",
            "Workshop-Daten: Bleiben-oder-Gehen-Entscheidungen, ausgewählte Bedrohungen, Prioritäten, Logistik, Reflexionen, Dokumentenauswahl, Blackout-Wissen, körperliche Einschränkungen, Informationsfilterung, Isolationsstabilität, Resilienzverhalten, Einladungsanfragen und Fortschritt.",
            "Lab-Fotodaten: Fotos, die Sie während einer Lab- oder Workshop-Fotoaufgabe freiwillig hochladen, einschließlich Speicherpfad und zugehöriger Dateimetadaten.",
            "Generierte Daten: Kontrol Scores, LAB Scores, Score-Bänder, Verfügbarkeitssignale, Assessment-Flags, Audit-Daten zu übersprungenen oder fehlenden Fragen, Empfehlungen, Modulabschluss, Aufgabenfortschritt und Verbesserungsdatensätze.",
            "Support- und Kontaktdaten: Nachrichten aus Support- oder Fragefunktionen sowie eingeladene E-Mail-Adressen, die Sie zum Einladen anderer Personen eingeben.",
          ],
        },
        {
          title: "2. Zweck der Verarbeitung",
          body: [
            "Wir verarbeiten Daten, um Konten zu erstellen und zu verwalten, E-Mails zu bestätigen, Passwörter zurückzusetzen, Konten mit Mitarbeiterprofilen zu verbinden und Kernfunktionen bereitzustellen.",
            "Assessment-, Workshop- und Moduldaten werden genutzt, um Scores zu berechnen, Fortschritt anzuzeigen, Empfehlungen zu erzeugen, Preparedness-Übungen zu unterstützen und individuelle oder gruppenbezogene Workshop-Ansichten bereitzustellen.",
            "Hochgeladene Lab-Fotos werden genutzt, um die Lab-Fotoaufgabe zu unterstützen, autorisierten Manager- oder Admin-Rollen die Ansicht von Fotos aus dem Lab zu ermöglichen und die freiwillige Teilnahme an dieser Aktivität zu dokumentieren.",
            "Unternehmens- und Rollendaten werden genutzt, um Berechtigungen anzuwenden, Manager- oder Admin-Dashboards zu ermöglichen, Workshops zu verwalten und Mitarbeitende der richtigen Organisation zuzuordnen.",
            "Support-Nachrichten und Einladungsanfragen werden genutzt, um Anfragen zu beantworten und Einladungs- bzw. Sharing-Funktionen zu unterstützen.",
          ],
        },
        {
          title: "3. Automatisierte Verarbeitung und Scores",
          body: [
            "Die Plattform wertet ausgewählte Antworten und Fortschrittsdaten automatisch aus, um Scores, Score-Bänder, Empfehlungen, Flags und nächste Schritte zu berechnen.",
            "Diese Ergebnisse basieren auf vordefinierten Bewertungsregeln und Logiken der Plattform und dienen der Information, Sensibilisierung und Vorbereitung.",
            "Die Ergebnisse haben keine rechtliche Wirkung, garantieren keine Sicherheit und ersetzen keine professionelle, medizinische, rechtliche oder Notfallberatung.",
          ],
        },
        {
          title: "4. Sichtbarkeit im Organisationskontext",
          body: [
            "Wenn Sie takeKONTROL über einen Arbeitgeber, ein Unternehmen, einen Workshop oder eine vergleichbare Organisation nutzen, kann Ihr Konto dieser Organisation zugeordnet werden.",
            "Manager und Admins können Daten sehen, die zur Durchführung von Workshops, Verwaltung der Teilnahme, Anzeige von Unternehmens-Dashboards und Einschätzung der Gruppenbereitschaft benötigt werden. Je nach implementierten Berechtigungen kann dies Profilfelder, Teilnahmestatus, Scores, ausgewählte Workshop-Antworten oder aggregierte Einblicke umfassen.",
            "Hochgeladene Lab-Fotos können autorisierten Manager- und Admin-Rollen in der Lab-Fotos-Galerie angezeigt werden.",
            "Einige Workshop-Ansichten zeigen Gruppenantworten oder Prozentwerte bewusst auch Teilnehmenden. Bei aktivierter Anonymität werden direkte identifizierende Profilfelder reduziert; einzelne Workshop-Auswahlen können dennoch Teil der Gruppenerfahrung sein.",
          ],
        },
        {
          title: "5. Speicherung und Dienstleister",
          body: [
            "Plattformdaten werden in Supabase gespeichert, einschließlich PostgreSQL-Datenbanktabellen, Supabase Auth, Edge Functions und Supabase Storage für Avatar-Dateien und freiwillig hochgeladene Lab-Fotos.",
            "E-Mails zur Bestätigung und zum Zurücksetzen von Passwörtern können über E-Mail-Dienstleister wie Resend versendet werden.",
            "Wir verkaufen keine personenbezogenen Daten. Eine Weitergabe erfolgt nur an Dienstleister, die für Betrieb, Sicherheit, Hosting, Wartung oder Support der Plattform erforderlich sind.",
          ],
        },
        {
          title: "6. Sicherheit",
          body: [
            "Wir nutzen Supabase-Authentifizierung, Zugriffskontrollen, Row-Level-Security-Richtlinien, rollenbasierte Berechtigungen und geschützte Edge Functions, um den Zugriff auf personenbezogene Daten zu begrenzen.",
            "Viele Tabellen beschränken Nutzer auf eigene Datensätze; Manager und Admins erhalten Zugriff, soweit dies für Organisations- und Workshopverwaltung erforderlich ist.",
            "Kein System kann vollständig sicher garantiert werden, wir ergreifen jedoch angemessene technische und organisatorische Schutzmaßnahmen.",
          ],
        },
        {
          title: "7. Speicherdauer und Löschung",
          body: [
            "Wir speichern personenbezogene Daten so lange, wie es für die Bereitstellung des Dienstes, die Kontoverwaltung, Workshops, gesetzliche Pflichten, Streitbeilegung und den Schutz der Plattformintegrität erforderlich ist.",
            "Freiwillig hochgeladene Lab-Fotos werden gespeichert, bis Sie deren Löschung verlangen, sofern sie nicht früher im Rahmen einer Kontolöschung, Workshopverwaltung oder Plattformwartung gelöscht werden.",
            "Wenn Ihr Konto ein Jahr lang nicht genutzt wird, gemessen an der letzten bekannten Anmeldung oder Sitzungsaktivität, löschen wir das Konto und die verknüpften Plattformdaten automatisch. Bei Konten ohne Anmeldung beginnt die Jahresfrist mit dem Erstellungsdatum des Kontos.",
            "Sie können, soweit verfügbar, die Löschung Ihres Kontos über die Plattform anfordern. Die Kontolöschung entfernt den Supabase-Auth-Benutzer und zugehörige Datensätze, die durch die Plattform kaskadierend oder ausdrücklich gelöscht werden.",
            "Aggregierte, anonymisierte, Backup-, Audit- oder gesetzlich erforderliche Datensätze können gespeichert bleiben, sofern sie Sie nicht mehr direkt identifizieren oder eine Aufbewahrung erforderlich ist.",
          ],
        },
        {
          title: "8. Ihre Rechte",
          body: [
            "Je nach anwendbarem Recht können Sie Rechte auf Auskunft, Berichtigung, Löschung, Einschränkung, Widerspruch gegen die Verarbeitung oder Datenübertragbarkeit haben.",
            "Bestimmte Profildaten können Sie in der Plattform aktualisieren, Anonymitätseinstellungen ändern, einen Export anfordern oder eine Löschung beantragen.",
            "Für Rechte, die nicht direkt im Produkt verfügbar sind, kontaktieren Sie uns bitte über die unten genannte E-Mail-Adresse.",
          ],
        },
        {
          title: "9. Cookies und lokaler Speicher",
          body: [
            "Wir verwenden technische Cookies, Browser-Speicher und Supabase-Session-Speicher, die für Authentifizierung, Sicherheit, Spracheinstellung, zwischengespeicherte Rollen und den Workshop-Ablauf erforderlich sind.",
            "Wir verwenden in der Plattform keine Werbe-Cookies.",
          ],
        },
        {
          title: "10. Änderungen dieser Erklärung",
          body: [
            "Wir können diese Datenschutzerklärung aktualisieren, um Produkt-, Datenmodell-, Anbieter- oder Rechtsänderungen abzubilden.",
            "Wesentliche Aktualisierungen werden in der Plattform verfügbar gemacht. Bei weiterer Nutzung gilt die aktuelle Erklärung für die zukünftige Nutzung.",
          ],
        },
        {
          title: "11. Kontakt",
          body: ["Bei Fragen zum Datenschutz kontaktieren Sie: info@takekontrol.de"],
        },
      ],
    },
  };

  const t = content[lang.toUpperCase() as "EN" | "DE"];

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",

        // 🌌 SAME BACKGROUND AS WORKSHOP
        background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
        `,
      }}
    >
      {/* 🔙 BACK BUTTON */}
      <Box
        sx={{
          position: "absolute",
          top: isTablet ? 12 : 24,
          left: isTablet ? 12 : 24,
          zIndex: 10,
        }}
      >
        <IconButton
          onClick={() => {
            if (role === "admin") {
              handlePageChange("ControlPanel");
            } else {
              handlePageChange("JourneyPillars");
            }
          }}
          sx={{
            color: "white",
            background: "rgba(255,255,255,0.06)",
            backdropFilter: "blur(6px)",
            border: "1px solid rgba(255,255,255,0.1)",
            boxShadow: "0 0 15px rgba(255,255,255,0.1)",

            "&:hover": {
              background: "rgba(255,255,255,0.12)",
            },
          }}
        >
          <HomeOutlinedIcon />
        </IconButton>
      </Box>

      {/* CONTENT */}
      <Box
        sx={{
          px: isTablet ? 2 : 4,
          py: isTablet ? 4 : 8,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 900,
            borderRadius: 5,
            p: isTablet ? 3 : 5,

            // ✨ GLASS CARD
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))",

            backdropFilter: "blur(14px)",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.4)",

            color: "white",
          }}
        >
          {/* TITLE */}
          <Typography
            variant={isTablet ? "h5" : "h4"}
            fontWeight={800}
            mb={2}
          >
            {t.title}
          </Typography>

          {/* UPDATED */}
          <Typography
            variant="body2"
            sx={{ opacity: 0.6, mb: 3 }}
          >
            {t.updated}: {t.updatedDate}
          </Typography>

          {/* INTRO */}
          <Typography sx={{ opacity: 0.9, mb: 3 }}>
            {t.intro}
          </Typography>

          {/* SECTIONS */}
          <Stack spacing={3}>
            {t.sections.map((section, i) => (
              <Box key={i}>
                <Typography fontWeight={700} mb={1}>
                  {section.title}
                </Typography>

                <Stack spacing={0.5}>
                  {section.body.map((line, j) => (
                    <Typography
                      key={j}
                      variant="body2"
                      sx={{ opacity: 0.8 }}
                    >
                      • {line}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
